/**
 * tab-reorder-math — pure snapshot-based drag-reorder math for SpacesTabStrip.
 *
 * Extracted from SpacesTabStrip to make the logic unit-testable and to keep
 * the component below the 400-line file-size cap.
 *
 * Algorithm (H1 fix):
 *   - All measurements are taken ONCE at drag-start via getBoundingClientRect
 *     snapshots, never during pointermove (eliminates M3 per-move measurement thrash).
 *   - Target index is computed by midpoint-crossing: the dragged tab's moving
 *     edge (leading edge in the drag direction) must cross a neighbour's midpoint
 *     before the target advances past that neighbour.
 *   - Neighbour displacement magnitude = dragged tab's snapshot width + inter-tab
 *     gap (the exact width of the vacated slot). This is constant and correct:
 *     only the dragged tab's slot is vacated; every displaced neighbour slides
 *     by exactly that slot width regardless of its own width.
 *   - Gap is read from the snapshot (consecutive left − width difference) rather
 *     than hardcoded.
 *
 * Immutability: all inputs are read-only; output is a new object each call.
 */

export interface TabSnapshot {
  /** Left edge of each tab wrapper (from getBoundingClientRect). */
  readonly lefts: readonly number[];
  /** Width of each tab wrapper (from getBoundingClientRect). */
  readonly widths: readonly number[];
  /** Gap between consecutive tabs (derived from snapshot). */
  readonly gap: number;
}

export interface ReorderResult {
  /** The new target drop index (clamped 0..n-1). */
  readonly targetIdx: number;
  /**
   * Pixel offset to apply to each displaced neighbour via CSS translateX.
   * Index i → translate value in px (0 = no displacement).
   * The dragged tab itself is NOT included here; the component applies raw deltaX
   * to it directly.
   */
  readonly displacedOffsets: readonly number[];
}

/**
 * Derive the inter-tab gap from the snapshot.
 * If there is only one tab, returns 0 (gap is irrelevant — no displacement possible).
 */
export function deriveGap(lefts: readonly number[], widths: readonly number[]): number {
  if (lefts.length < 2) return 0;
  // gap = left[1] - (left[0] + width[0])
  // Safe: we just checked length >= 2.
  const left0 = lefts[0] ?? 0;
  const left1 = lefts[1] ?? 0;
  const width0 = widths[0] ?? 0;
  return left1 - (left0 + width0);
}

/**
 * Build a TabSnapshot from raw BoundingClientRect arrays.
 *
 * @param lefts   Left edges from getBoundingClientRect(), one per tab wrapper.
 * @param widths  Widths from getBoundingClientRect(), one per tab wrapper.
 */
export function makeSnapshot(lefts: readonly number[], widths: readonly number[]): TabSnapshot {
  return { lefts, widths, gap: deriveGap(lefts, widths) };
}

/**
 * Compute the current reorder state given a snapshot and the current deltaX.
 *
 * @param snap       Snapshot captured at drag start.
 * @param originIdx  Index of the tab being dragged.
 * @param deltaX     Current horizontal displacement from the drag-start position (px).
 *
 * Slop (8px dead-zone) is handled in the component before calling this;
 * this function treats any deltaX as intentional.
 */
export function computeReorder(
  snap: TabSnapshot,
  originIdx: number,
  deltaX: number,
): ReorderResult {
  const n = snap.lefts.length;

  // Displacement magnitude for each displaced neighbour:
  // the dragged tab's slot width = its own width + the gap on one side.
  const originWidth = snap.widths[originIdx] ?? 0;
  const slotWidth = originWidth + snap.gap;

  // Compute target index via midpoint-crossing.
  let targetIdx = originIdx;

  if (deltaX > 0) {
    // Dragging right: check neighbours to the RIGHT one by one.
    // The dragged tab's RIGHT edge is: originRight + deltaX
    const originLeft = snap.lefts[originIdx] ?? 0;
    const originRight = originLeft + originWidth;
    for (let i = originIdx + 1; i < n; i++) {
      const nLeft = snap.lefts[i] ?? 0;
      const nWidth = snap.widths[i] ?? 0;
      const neighbourMid = nLeft + nWidth / 2;
      if (originRight + deltaX > neighbourMid) {
        targetIdx = i;
      } else {
        break; // tabs are ordered left-to-right; once we don't cross, stop
      }
    }
  } else if (deltaX < 0) {
    // Dragging left: check neighbours to the LEFT one by one.
    // The dragged tab's LEFT edge is: originLeft + deltaX
    const originLeft = snap.lefts[originIdx] ?? 0;
    for (let i = originIdx - 1; i >= 0; i--) {
      const nLeft = snap.lefts[i] ?? 0;
      const nWidth = snap.widths[i] ?? 0;
      const neighbourMid = nLeft + nWidth / 2;
      if (originLeft + deltaX < neighbourMid) {
        targetIdx = i;
      } else {
        break;
      }
    }
  }

  // Clamp (defensive — loop bounds already guarantee this, but belt-and-suspenders).
  targetIdx = Math.max(0, Math.min(n - 1, targetIdx));

  // Compute displacement for each neighbour.
  // Only tabs between originIdx and targetIdx (exclusive of origin) are displaced.
  const displacedOffsets = snap.widths.map((_, i) => {
    if (i === originIdx) return 0;
    if (originIdx < targetIdx) {
      // Dragging right: tabs in (originIdx, targetIdx] shift LEFT by slotWidth.
      if (i > originIdx && i <= targetIdx) return -slotWidth;
    } else if (originIdx > targetIdx) {
      // Dragging left: tabs in [targetIdx, originIdx) shift RIGHT by slotWidth.
      if (i >= targetIdx && i < originIdx) return slotWidth;
    }
    return 0;
  });

  return { targetIdx, displacedOffsets };
}
